import { FormEvent, useEffect, useState } from 'react';
import {
  getPaymentCredentials,
  PaymentCredentials,
  PaymentEnv,
  SecretField,
  setPaymentMode,
  StoreSummary,
  updatePaymentCredentials,
} from '../../api/admin';

const ENV_LABELS: Record<PaymentEnv, string> = { sandbox: '샌드박스(테스트)', production: '운영' };

const EMPTY_FORM = {
  kakaoCid: '',
  kakaoSecretKey: '',
  naverPartnerId: '',
  naverClientId: '',
  naverClientSecret: '',
  naverChainId: '',
};

interface Props {
  store: StoreSummary;
  onClose: () => void;
  onSaved: () => void;
}

// 매장별 카카오페이·네이버페이 가맹점 키. 시크릿은 서버에서 다시 읽을 수 없으므로 입력할 때만 바뀐다.
function PaymentCredentialsPanel({ store, onClose, onSaved }: Props) {
  const [currentEnv, setCurrentEnv] = useState<PaymentEnv>('sandbox');
  const [env, setEnv] = useState<PaymentEnv>('sandbox');
  const [saved, setSaved] = useState<Record<PaymentEnv, PaymentCredentials> | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [clearSecrets, setClearSecrets] = useState<SecretField[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);

  const fillForm = (creds: PaymentCredentials) => {
    setForm({
      ...EMPTY_FORM,
      kakaoCid: creds.kakaoCid,
      naverPartnerId: creds.naverPartnerId,
      naverClientId: creds.naverClientId,
      naverChainId: creds.naverChainId,
    });
    setClearSecrets([]);
  };

  useEffect(() => {
    setSaved(null);
    setError(null);
    setMessage(null);
    getPaymentCredentials(store.id)
      .then((res) => {
        const byEnv = Object.fromEntries(res.credentials.map((c) => [c.env, c])) as Record<
          PaymentEnv,
          PaymentCredentials
        >;
        setCurrentEnv(res.currentEnv);
        setEnv(res.currentEnv);
        setSaved(byEnv);
        fillForm(byEnv[res.currentEnv]);
      })
      .catch((err: any) => setError(err.response?.data?.message || '결제 키를 불러오지 못했습니다.'));
  }, [store.id]);

  const switchEnv = (next: PaymentEnv) => {
    if (!saved) return;
    setEnv(next);
    setMessage(null);
    setError(null);
    fillForm(saved[next]);
  };

  const toggleClear = (field: SecretField) => {
    setClearSecrets((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const updated = await updatePaymentCredentials(store.id, env, { ...form, clearSecrets });
      setSaved((prev) => (prev ? { ...prev, [env]: updated } : prev));
      fillForm(updated);
      setMessage('저장했습니다.');
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || '결제 키 저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModeChange = async (mode: PaymentEnv) => {
    const warning =
      mode === 'production'
        ? `${store.name}을(를) 운영 모드로 전환합니다. 이후 고객 결제는 실제로 청구됩니다. 계속할까요?`
        : `${store.name}을(를) 샌드박스 모드로 전환합니다. 이후 고객 결제는 테스트 결제가 되어 매출이 발생하지 않습니다. 계속할까요?`;
    if (!window.confirm(warning)) return;
    setError(null);
    setMessage(null);
    setSwitchingMode(true);
    try {
      await setPaymentMode(store.id, mode);
      setCurrentEnv(mode);
      setMessage(`결제 모드를 ${ENV_LABELS[mode]}(으)로 전환했습니다.`);
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || '결제 모드 전환에 실패했습니다.');
    } finally {
      setSwitchingMode(false);
    }
  };

  const creds = saved?.[env];
  const secretHint = (isSet: boolean | undefined, field: SecretField) =>
    clearSecrets.includes(field) ? '저장 시 삭제됨' : isSet ? '등록됨 — 바꿀 때만 입력' : '미등록';

  return (
    <section className="StoreManagementScreen-panel">
      <div className="StoreManagementScreen-panelHeader">
        <h2>결제 키 설정 — {store.name}</h2>
        <button type="button" className="StoreManagementScreen-linkButton" onClick={onClose}>
          닫기
        </button>
      </div>

      {saved && (
        <div className={`StoreManagementScreen-mode ${currentEnv}`}>
          <span>
            현재 결제 모드: <strong>{ENV_LABELS[currentEnv]}</strong>
            {currentEnv === 'sandbox' ? ' — 고객 결제가 실제로 청구되지 않습니다.' : ' — 고객 결제가 실제로 청구됩니다.'}
          </span>
          <button
            type="button"
            onClick={() => handleModeChange(currentEnv === 'sandbox' ? 'production' : 'sandbox')}
            disabled={switchingMode}
          >
            {currentEnv === 'sandbox' ? '운영으로 전환' : '샌드박스로 전환'}
          </button>
        </div>
      )}

      <div className="StoreManagementScreen-envTabs">
        {(Object.keys(ENV_LABELS) as PaymentEnv[]).map((key) => (
          <button
            key={key}
            type="button"
            className={key === env ? 'active' : ''}
            onClick={() => switchEnv(key)}
            disabled={!saved}
          >
            {ENV_LABELS[key]}
            {key === currentEnv && ' · 사용 중'}
          </button>
        ))}
      </div>

      {!saved && !error && <p>불러오는 중...</p>}

      {saved && (
        <form className="StoreManagementScreen-form" onSubmit={handleSubmit} autoComplete="off">
          <fieldset>
            <legend>카카오페이</legend>
            <label>
              CID
              <input
                value={form.kakaoCid}
                placeholder={env === 'sandbox' ? '샌드박스는 TC0ONETIME 고정' : '제휴 후 발급받은 CID'}
                onChange={(e) => setForm({ ...form, kakaoCid: e.target.value })}
                disabled={env === 'sandbox'}
              />
            </label>
            <label>
              Secret key ({env === 'sandbox' ? 'dev' : 'prod'})
              <input
                type="password"
                value={form.kakaoSecretKey}
                placeholder={secretHint(creds?.kakaoSecretKeySet, 'kakaoSecretKey')}
                onChange={(e) => setForm({ ...form, kakaoSecretKey: e.target.value })}
              />
            </label>
            {creds?.kakaoSecretKeySet && (
              <label className="StoreManagementScreen-checkbox">
                <input
                  type="checkbox"
                  checked={clearSecrets.includes('kakaoSecretKey')}
                  onChange={() => toggleClear('kakaoSecretKey')}
                />
                Secret key 삭제
              </label>
            )}
          </fieldset>

          <fieldset>
            <legend>네이버페이</legend>
            <label>
              Partner ID
              <input value={form.naverPartnerId} onChange={(e) => setForm({ ...form, naverPartnerId: e.target.value })} />
            </label>
            <label>
              Client ID
              <input value={form.naverClientId} onChange={(e) => setForm({ ...form, naverClientId: e.target.value })} />
            </label>
            <label>
              Client Secret
              <input
                type="password"
                value={form.naverClientSecret}
                placeholder={secretHint(creds?.naverClientSecretSet, 'naverClientSecret')}
                onChange={(e) => setForm({ ...form, naverClientSecret: e.target.value })}
              />
            </label>
            <label>
              Chain ID
              <input value={form.naverChainId} onChange={(e) => setForm({ ...form, naverChainId: e.target.value })} />
            </label>
            {creds?.naverClientSecretSet && (
              <label className="StoreManagementScreen-checkbox">
                <input
                  type="checkbox"
                  checked={clearSecrets.includes('naverClientSecret')}
                  onChange={() => toggleClear('naverClientSecret')}
                />
                Client Secret 삭제
              </label>
            )}
          </fieldset>

          {error && <p className="StoreManagementScreen-error">{error}</p>}
          {message && <p className="StoreManagementScreen-message">{message}</p>}
          <button type="submit" disabled={submitting}>
            {submitting ? '저장 중...' : `${ENV_LABELS[env]} 키 저장`}
          </button>
          {creds?.updatedAt && (
            <span className="StoreManagementScreen-meta">
              마지막 수정 {new Date(creds.updatedAt).toLocaleString()}
            </span>
          )}
        </form>
      )}

      {!saved && error && <p className="StoreManagementScreen-error">{error}</p>}
    </section>
  );
}

export default PaymentCredentialsPanel;
